import React from 'react';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Lead, LeadFollowUp, LeadStatus, LeadSubStage, LeadTimelineEvent } from '../../types/lead';
import StatusBadge from '../UI/StatusBadge';
import FollowUpDateEditor from './FollowUpDateEditor';

const PIPELINE_STAGES: { id: LeadStatus; label: string; activeClass: string }[] = [
  { id: 'interested', label: 'Interested', activeClass: 'border-green-500 bg-green-500/10 text-green-600 dark:text-green-400' },
  { id: 'follow_up', label: 'Follow Up', activeClass: 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  { id: 'app_out', label: 'App Out', activeClass: 'border-indigo-500 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  { id: 'app_in', label: 'App In', activeClass: 'border-sky-500 bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  { id: 'submitted', label: 'Submitted', activeClass: 'border-violet-500 bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  { id: 'contract', label: 'Contract', activeClass: 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  { id: 'funded', label: 'Funded', activeClass: 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  { id: 'not_interested', label: 'Not Interested', activeClass: 'border-muted-foreground bg-muted/40 text-muted-foreground' },
];

const PIPELINE_EVENT_TYPES = new Set([
  'status_change',
  'sub_stage_change',
  'follow_up_added',
]);

interface LeadPipelinePanelProps {
  lead: Lead;
  subStages: LeadSubStage[];
  followUps: LeadFollowUp[];
  timeline: LeadTimelineEvent[];
  saving?: boolean;
  onStatusChange: (status: LeadStatus) => void;
  onSubStageChange: (subStageId: string | null) => void;
  onFollowUpUpdated: () => void;
  followUpReadOnly?: boolean;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const describePipelineEvent = (event: LeadTimelineEvent): string => {
  const p = event.payload;
  switch (event.type) {
    case 'status_change':
      // Headline already shows the new status badge; only add the from→to detail.
      return p.from && p.to ? `${String(p.from).replace(/_/g, ' ')} → ${String(p.to).replace(/_/g, ' ')}` : '';
    case 'sub_stage_change':
      return p.subStageName ? `Sub-stage: ${String(p.subStageName)}` : 'Sub-stage updated';
    case 'follow_up_added':
      return p.scheduledAt ? `Follow-up: ${formatDate(String(p.scheduledAt))}` : 'Follow-up scheduled';
    default:
      return event.type.replace(/_/g, ' ');
  }
};

const PIPELINE_EVENT_LABELS: Record<string, string> = {
  status_change: 'Status changed',
  sub_stage_change: 'Sub-stage changed',
  follow_up_added: 'Follow-up scheduled',
};

const LeadPipelinePanel: React.FC<LeadPipelinePanelProps> = ({
  lead,
  subStages,
  followUps,
  timeline,
  saving = false,
  onStatusChange,
  onSubStageChange,
  onFollowUpUpdated,
  followUpReadOnly = false,
}) => {
  // Oldest → newest so the journey reads top-to-bottom (Interested → Follow Up → App…).
  const journey = timeline
    .filter((e) => PIPELINE_EVENT_TYPES.has(e.type))
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return (
    <div className="space-y-5">
      {/* Current stage context — status / sub-stage / follow-up are changed from the
          controls in the panel header above; this tab is the read-only pipeline log. */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground">Current stage</h4>
          <StatusBadge status={lead.status} type="lead" size="sm" />
        </div>
        {(lead.subStage?.name || lead.followUpDate) && (
          <div className="mt-2 space-y-1">
            {lead.subStage?.name && (
              <p className="text-xs text-muted-foreground">
                Sub-stage: <span className="text-foreground">{lead.subStage.name}</span>
              </p>
            )}
            {lead.followUpDate && (
              <p className="text-xs text-muted-foreground">
                Next follow-up: <span className="text-foreground">{formatDate(String(lead.followUpDate))}</span>
              </p>
            )}
          </div>
        )}
      </section>

      {/* Pipeline log — full status journey with date & time */}
      <section className="rounded-xl border border-border bg-card p-4">
        <h4 className="mb-4 text-sm font-semibold text-foreground">Pipeline log</h4>
        {journey.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center">
            <ClockIcon className="mx-auto mb-2 h-7 w-7 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">No status changes logged yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Every status, sub-stage and follow-up change will appear here with date &amp; time.
            </p>
          </div>
        ) : (
          <ol className="relative">
            {journey.map((event, index) => {
              const isLast = index === journey.length - 1;
              const toStatus =
                event.type === 'status_change' ? (event.payload?.to as string | undefined) : undefined;
              const detail = describePipelineEvent(event);
              const dotClass =
                event.type === 'status_change'
                  ? 'bg-primary'
                  : event.type === 'follow_up_added'
                  ? 'bg-amber-500'
                  : 'bg-indigo-500';

              return (
                <li key={event.id} className="relative flex gap-3 pb-5 last:pb-0">
                  {!isLast && (
                    <span className="absolute left-[13px] top-7 bottom-0 w-px bg-border" aria-hidden />
                  )}
                  <span
                    className={`relative z-[1] mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-card ${dotClass}`}
                  >
                    {event.type === 'follow_up_added' ? (
                      <ClockIcon className="h-4 w-4 text-white" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {toStatus ? (
                        <>
                          <span className="text-sm font-medium text-foreground">Moved to</span>
                          <StatusBadge status={toStatus} type="lead" size="sm" />
                        </>
                      ) : (
                        <span className="text-sm font-medium text-foreground">
                          {PIPELINE_EVENT_LABELS[event.type] ?? event.type.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {detail && <p className="mt-1 text-xs capitalize text-muted-foreground">{detail}</p>}
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {event.createdByName && (
                        <span className="font-medium text-foreground/80">{event.createdByName} · </span>
                      )}
                      {formatDate(event.createdAt)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
};

export default LeadPipelinePanel;
